import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { BANDS_DJS_TAXONOMY_KEY } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { formatDateFrom } from '@zola-helpers/client/dist/es/util/dateUtils';
import LinkV2 from '@zola/zola-ui/src/components/LinkV2/LinkV2';
import StarRatings from '@zola/zola-ui/src/components/StarRatings/StarRatings';
import { HeartIcon } from '@zola/zola-ui/src/components/SvgIconsV3/Heart';
import { TagV2Size } from '@zola/zola-ui/src/components/Tag/TagV2.styles';
import { ViewMore } from '@zola/zola-ui/src/components/ViewMore/ViewMore';
import { useEffectOnce } from '@zola/zola-ui/src/hooks';
import { COLORS3, FONT } from '@zola/zola-ui/src/styles/emotion';
import MEDIA_QUERY_V2 from '@zola/zola-ui/src/styles/emotion/mediaQueryV2';
import { concealScrollbars } from '@zola/zola-ui/src/styles/emotion/utils';
import P from '@zola/zola-ui/src/typography/Paragraphs';

import styled from '@emotion/styled';

import { goToVendorFavoriteOnboarding } from '~/actions/favoriteActions';
import BestOfZolaPng from '~/assets/images/badges/best_of_zola.png';
import { doTrackVendorCardClickAndView } from '~/contexts/TrackingContracts/trackingContractsHelpers';
import { filterSupportedAwards } from '~/meta/awards';
import { useSearchResultsPageContext } from '~/pages/couples/explore/components/SearchResults/contexts/SearchResultsPageContext';
import { formatLocation } from '~/util/formatters';
import { formatRating } from '~/util/reviewUtils';
import getCanonicalUrl from '~/util/storefrontUrl';

import CardLinkWrapper from '../components/CardLinkWrapper';
import {
  getDisplayPrice,
  getPerPriceText,
  getVendorsPerPrice,
  showPerEntityPricing,
} from '../util/helper';
import { VendorCardVendor } from '../util/types';
import { getCardDetailsMeta } from '../VendorCard/components/CardDetails';
import { VendorCardPhoto, wasCardPhotoClick } from '../VendorCard/VendorCardPhoto';

const LONG_CONTENT_MAX_WIDTH = 740;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const Hed = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 2px;

  ${MEDIA_QUERY_V2.EXCLUDE_DESKTOP} {
    justify-content: space-between;
  }
`;

const DESCRIPTION_LINE_HEIGHT = 20;

const VendorName = styled.div`
  font-size: 20px;
  line-height: 32px;
  font-weight: ${FONT.FONT_WEIGHT_SEMI_BOLD};
`;

const Description = styled(P.BodySmall)`
  &&& {
    max-width: ${LONG_CONTENT_MAX_WIDTH}px;
    font-size: 14px;
    line-height: ${DESCRIPTION_LINE_HEIGHT}px;
  }
`;

const ScrollableContainer = styled.div<{
  withBottomGradient: boolean;
  withTopGradient: boolean;
  gradientHeight: number;
}>`
  ${concealScrollbars};
  overflow-y: auto;

  ${({ withBottomGradient, withTopGradient, gradientHeight }) =>
    withBottomGradient &&
    !withTopGradient &&
    `
    mask-image: linear-gradient(to bottom, black calc(100% - ${gradientHeight}px), transparent 100%)
  `}

  ${({ withBottomGradient, withTopGradient, gradientHeight }) =>
    !withBottomGradient &&
    withTopGradient &&
    `
    mask-image: linear-gradient(to bottom, transparent 0%, black ${gradientHeight}px);
  `}

${({ withBottomGradient, withTopGradient, gradientHeight }) =>
    withBottomGradient &&
    withTopGradient &&
    `
    mask-image: linear-gradient(to bottom, transparent 0%, black ${gradientHeight}px, black calc(100% - ${gradientHeight}px), transparent 100%);
  `}
`;
const Scrollable: React.FC<{ height: number; gradientHeight: number }> = ({
  children,
  height,
  gradientHeight,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);

  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
  const [isScrolledToTop, setIsScrolledToTop] = useState(true);

  const setScrolled = useCallback(() => {
    if (ref.current) {
      const { scrollHeight, clientHeight, scrollTop } = ref.current;
      const isScrollable = scrollHeight > clientHeight;
      if (isScrollable) {
        const bob = scrollHeight - clientHeight === scrollTop;
        setIsScrolledToBottom(bob);
        setIsScrolledToTop(bob ? false : scrollTop === 0);
      } else {
        setIsScrolledToTop(true);
        setIsScrolledToBottom(true);
      }
    }
  }, []);

  useEffectOnce(() => {
    setScrolled();
  });

  useEffect(() => {
    const { current } = ref;
    if (current) {
      current.addEventListener('scroll', setScrolled);
    }
    return () => {
      current?.removeEventListener('scroll', setScrolled);
    };
  }, [ref, setScrolled]);

  return (
    <ScrollableContainer
      withBottomGradient={!isScrolledToBottom}
      withTopGradient={!isScrolledToTop}
      gradientHeight={gradientHeight}
      ref={ref}
      style={{ maxHeight: height }}
    >
      {children}
    </ScrollableContainer>
  );
};

const Review = styled.div`
  background-color: ${COLORS3.BLACK_005};
  border-radius: 16px;
  color: ${COLORS3.BLACK_075};
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;
  margin-top: 16px;
`;

const REVIEW_LINE_HEIGHT = 20;

const ReviewContent = styled(P.BodySmall)`
  max-width: ${LONG_CONTENT_MAX_WIDTH}px;
  &&& {
    color: ${COLORS3.BLACK_075};
    font-size: 14px;
    line-height: ${REVIEW_LINE_HEIGHT}px;
  }
`;

const AuthorInfo = styled.span`
  font-size: 14px;
  font-weight: ${FONT.FONT_WEIGHT_SEMI_BOLD};
  line-height: 20px;
`;

const RatingAndLocation = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Rating = ({ vendor }: { vendor: VendorCardVendor }): JSX.Element => {
  const { averageReviewsRate, reviewsCount } = vendor;

  if (!averageReviewsRate) {
    return <Fragment />;
  }
  return (
    <StarRatings
      activeStars={formatRating(averageReviewsRate)}
      totalStars={5}
      fillColor={COLORS3.YELLOW_100}
      starSize="default"
      textSize="default"
      reviewCount={reviewsCount || 0}
      showReviewCount={false}
    />
  );
};

const Location = ({ vendor }: { vendor: VendorCardVendor }) => {
  if (vendor.city && vendor.stateProvince) {
    return <span data-test-id="location">{formatLocation(vendor)}</span>;
  }
  return <Fragment />;
};

const DetailList = styled.ul`
  padding: 0;
  margin: 4px 0;
  list-style: none;
`;

const DetailItem = styled.li`
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: ${FONT.FONT_WEIGHT_SEMI_BOLD};

  &:not(:last-of-type) {
    margin-bottom: 4px;
  }
`;

const CardDetails = ({ vendor }: { vendor: VendorCardVendor }): JSX.Element => {
  const { vendorSearch } = useSearchResultsPageContext();
  const { selectedLocation } = vendorSearch;
  const queriedLocation = selectedLocation?.label || '';

  const cardDetailMeta = useMemo(() => {
    const { price, taxonomyNodeKey, priceRangeVisible, details } = vendor;
    const hasNoMinumumPrice = price?.min === null || price?.min === undefined;
    const whichDisplayPrice = showPerEntityPricing(taxonomyNodeKey, price)
      ? getVendorsPerPrice(taxonomyNodeKey, details)
      : getDisplayPrice(price?.min);

    const displayPrice = priceRangeVisible ? whichDisplayPrice : null;

    const perPriceCopy =
      hasNoMinumumPrice && taxonomyNodeKey ? getPerPriceText(taxonomyNodeKey) : '';

    const fullDetailList = getCardDetailsMeta({
      vendor,
      displayPrice,
      queriedLocation,
      perPriceCopy,
    });

    return fullDetailList.filter((meta) => {
      if (!meta.isCategory) return true;

      // No search category, show the category on the card
      if (!vendorSearch.selectedTaxonomy) return true;

      // Always show the category for musicians
      return taxonomyNodeKey === BANDS_DJS_TAXONOMY_KEY;
    });
  }, [queriedLocation, vendor, vendorSearch.selectedTaxonomy]);

  return (
    <DetailList>
      {cardDetailMeta.map((meta, index) => {
        return (
          <DetailItem key={index}>
            {meta.icon({ height: 20, width: 20, showTitle: false })}
            <span data-testid={meta.dataTestId}>{meta.longLabel || meta.label}</span>
          </DetailItem>
        );
      })}
    </DetailList>
  );
};

const VendorCard = styled(CardLinkWrapper)<{ blur: boolean }>`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 24px;
  text-decoration: none;
  font-weight: ${FONT.FONT_WEIGHT_REGULAR};

  ${({ blur }) =>
    blur &&
    `
    > * {
      filter: blur(4px);
      opacity: 0.5;
      pointer-events: none;
    }
  `}

  &:hover, &:active {
    text-decoration: none !important;
  }

  border-top: 1px solid ${COLORS3.BLACK_030};
  &:last-of-type {
    border-bottom: 1px solid ${COLORS3.BLACK_030};
  }
  padding: 16px 0;

  ${MEDIA_QUERY_V2.MOBILE} {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  @media (hover: hover) {
    &:hover {
      .vendor-card-photo {
        transform: scale(1.04);
      }

      ${Hed} {
        text-decoration: underline;
        cursor: pointer;
      }
    }
  }
`;

const FeaturedReview = ({ vendor }: { vendor: VendorCardVendor }) => {
  const { featuredReview } = vendor;

  const authorInfo: string = useMemo(() => {
    const parts = [];
    if (featuredReview) {
      parts.push(featuredReview.reviewerName);

      if (featuredReview.createdAt) {
        parts.push(formatDateFrom(featuredReview.createdAt, new Date()));
      }
    }
    return parts.join(', ');
  }, [featuredReview]);

  if (featuredReview) {
    return (
      <Review>
        <ReviewContent component="div">
          <ViewMore
            backgroundColor={COLORS3.BLACK_005}
            lines={2}
            viewLess={() => <Fragment />}
            viewMore={({ setIsOpen }) => (
              <LinkV2
                sizes="smaller"
                role="button"
                noTextTransform
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setIsOpen(true);
                }}
              >
                Read more
              </LinkV2>
            )}
          >
            <P.BodySmall component="div" color="BLACK_075">
              &ldquo;{featuredReview.reviewText}&rdquo;
            </P.BodySmall>
          </ViewMore>
        </ReviewContent>
        <AuthorInfo>{authorInfo}</AuthorInfo>
      </Review>
    );
  }

  return <Fragment />;
};

const BestOfZola = ({ vendor }: { vendor: VendorCardVendor }) => {
  const { awards } = vendor;

  const hasAward = useMemo(() => {
    return filterSupportedAwards(awards).length > 0;
  }, [awards]);

  if (!hasAward) {
    return <Fragment />;
  }

  return <img alt="Awarded Best of Zola" height={56} src={BestOfZolaPng} width={56} />;
};

const VendorDetails = styled.div`
  display: flex;
  gap: 24px;
  justify-content: space-between;
  align-items: flex-start;
`;

const FavoriteLink = styled(LinkV2)`
  padding: 6px; // makes the whole thing 32px, but easier to tap
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  svg {
    flex-shrink: 0;
  }

  transition: color 0.3s ease;
  &:hover {
    color: #feaaa8;
  }
`;
const FavoriteHeart = ({ vendor }: { vendor: VendorCardVendor }) => {
  const { storefrontUuid, vendorName } = vendor;
  if (storefrontUuid) {
    return (
      <FavoriteLink
        role="button"
        type="button"
        aria-label={`Favorite ${vendorName}`}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          goToVendorFavoriteOnboarding(storefrontUuid);
        }}
      >
        <HeartIcon showTitle={false} height={20} width={20} fillOpacity="1" />
      </FavoriteLink>
    );
  }
  return <Fragment />;
};

export const PreAuthVendorCard = ({
  vendor,
  position,
  loading,
}: {
  vendor: VendorCardVendor;
  position: number;
  loading: boolean;
}): JSX.Element => {
  const { storefrontSlug, taxonomyNodeKey } = vendor;

  const listingPagePath = useMemo(() => {
    return storefrontSlug && taxonomyNodeKey
      ? getCanonicalUrl(taxonomyNodeKey, storefrontSlug)
      : null;
  }, [storefrontSlug, taxonomyNodeKey]);

  /** Click handler for the photo to prevent navigation / click tracking when the carousel is used */
  const onPhotoClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    if (!wasCardPhotoClick(e)) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  /** Click handler for the card: just tracking, let the default behavior navigate */
  const onCardClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      // This is a great mystery.  Without this, returning to the SRP and hovering
      // a _different card_ causes this card to enter a hover/active state and show
      // text underlines, evn through we've disabled text-underlines on hover.
      e.currentTarget.blur();

      doTrackVendorCardClickAndView({
        vendor,
        position,
        listingShelfName: 'SEARCH_RESULTS_PAGE',
        includeTags: true,
      });
    },
    [position, vendor]
  );

  return (
    <VendorCard to={listingPagePath} onClick={onCardClick} blur={loading}>
      <VendorCardPhoto
        vendor={vendor}
        scaleOnHover={false}
        altScaleOnHover={undefined}
        showBadges={true}
        tagSize={TagV2Size.REGULAR}
        showCarousel={true}
        carouselPhotoIds={[]}
        lazyload={false}
        readOnly={false}
        scrollContainer={undefined}
        className="vendor-card-photo"
        onClick={onPhotoClick}
      />

      <Body data-testid="search-result">
        <Hed>
          <VendorName data-testid="vendor-name">{vendor.vendorName}</VendorName>
          <FavoriteHeart vendor={vendor} />
        </Hed>
        <RatingAndLocation>
          <Rating vendor={vendor} />
          <Location vendor={vendor} />
        </RatingAndLocation>
        <CardDetails vendor={vendor} />
        <VendorDetails>
          <Scrollable height={4 * DESCRIPTION_LINE_HEIGHT} gradientHeight={DESCRIPTION_LINE_HEIGHT}>
            <Description color="BLACK_075">{vendor.description}</Description>
          </Scrollable>
          <BestOfZola vendor={vendor} />
        </VendorDetails>
        <FeaturedReview vendor={vendor} />
      </Body>
    </VendorCard>
  );
};
